import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { destinatarioPerGeneraFattura, normalizeCodiceSdi } from "../utils/destinatarioFatturazione";
import { toastSdiError } from "../utils/sdiErrorMessages";
import {
  Receipt, Euro, FileText, Send, History, Check, Clock, AlertCircle,
  Download, CreditCard, Loader2, RefreshCw, ChevronDown, ChevronUp,
  Bell, X, Plus, Package, ClipboardList, Calculator, ExternalLink,
  Banknote, AlertTriangle, CheckCircle, XCircle, Trash2, Pencil, Paperclip,
  Search, CheckCircle2, Eye, Link2, Unlink,
} from "lucide-react";
import CollegaFicDialog from "./CollegaFicDialog";
import ConfermaSedeFicDialog from "./ConfermaSedeFicDialog";
import FatturaAllegatiSection from "./FatturaAllegatiSection";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Textarea } from "./ui/textarea";

function parseIndirizzoResidenza(raw) {
  const s = String(raw || "").trim();
  if (!s) {
    return { indirizzo: "", cap: "", citta: "", provincia: "" };
  }

  const capComuneRe = /(?<![0-9])(?<cap>\d{5})\s+(?<city>[A-Za-zÀ-ÖØ-öø-ÿ][A-Za-zÀ-ÖØ-öø-ÿ'.\s-]+?)(?:\s+\((?<prov1>[A-Za-z]{2})\)|\s+(?<prov2>[A-Za-z]{2}))?\s*$/iu;
  const m = s.match(capComuneRe);
  if (m) {
    const indirizzo = s.slice(0, m.index).replace(/,\s*(SNC|SN)\b/gi, " $1").replace(/\s+/g, " ").trim(" ,");
    const citta = (m.groups.city || "").replace(/^(?:SNC|SN|N\.?\s*)\s+/i, "").trim();
    return {
      indirizzo,
      cap: m.groups.cap || "",
      citta,
      provincia: (m.groups.prov1 || m.groups.prov2 || "").toUpperCase(),
    };
  }

  const parts = s.split(",").map((p) => p.trim()).filter(Boolean);
  if (parts.length >= 2) {
    const indirizzo = parts[0].replace(/,\s*(SNC|SN)\b/gi, " $1").trim();
    const tail = parts.slice(1).join(", ");
    const mTail = tail.match(capComuneRe);
    if (mTail) {
      const citta = (mTail.groups.city || "").replace(/^(?:SNC|SN|N\.?\s*)\s+/i, "").trim();
      return {
        indirizzo,
        cap: mTail.groups.cap || "",
        citta,
        provincia: (mTail.groups.prov1 || mTail.groups.prov2 || "").toUpperCase(),
      };
    }
  }

  const inline = s.match(/^(?<street>.+?)\s+(?<cap>\d{5})\s+(?<city>.+?)(?:\s+(?<prov>[A-Za-z]{2}))?\s*$/i);
  if (inline) {
    return {
      indirizzo: inline.groups.street.trim(),
      cap: inline.groups.cap,
      citta: inline.groups.city.replace(/^(?:SNC|SN|N\.?\s*)\s+/i, "").trim(),
      provincia: (inline.groups.prov || "").toUpperCase(),
    };
  }

  return { indirizzo: s, cap: "", citta: "", provincia: "" };
}
import ModificaVociFatturaDialog from "./ModificaVociFatturaDialog";
import { voceDescrizionePerUI } from "../utils/fatturazioneLabels";
import { preferenzeToGeneraFormRetta } from "../utils/preferenzeFatturazioneRetta";
import {
  fatturaEliminabile,
  fatturaEliminaSuFic,
  fatturaModificabile,
  fatturaInCodaSdi,
} from "../utils/fatturazioneSdi";
import { isFicSedePropostaError, ficSedePropostaFromError, postInviaSdi, formatFicSedeDisplay } from "../utils/ficSedeInvio";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MESI = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
];

function getMeseCorrente() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function formatMese(mese) {
  if (!mese) return "";
  const [year, month] = mese.split("-");
  return `${MESI[parseInt(month) - 1]} ${year}`;
}

/** Parametri retta per anteprima/generazione fattura dal fascicolo. */
function buildRettaApiParams(form, { hasVociRettaConfig = false } = {}) {
  const params = {
    includi_retta: !!form.includi_retta,
    // Il dialog ha già risolto le preferenze fascicolo in UI: non riapplicarle server-side.
    applica_preferenze_retta: false,
  };
  if (!form.includi_retta) return params;
  if (form.retta_modalita === "parziale") {
    const n = parseFloat(String(form.importo_retta_parziale || "").replace(",", "."));
    if (Number.isFinite(n) && n > 0) params.importo_retta = n;
  }
  if (form.retta_modalita === "senza_importo") {
    params.retta_senza_importo = true;
  }
  if (hasVociRettaConfig && Array.isArray(form.voci_retta_incluse)) {
    params.voci_retta_incluse = form.voci_retta_incluse.join(",");
  }
  return params;
}

function buildGeneraPayload(form, mese, { hasVociRettaConfig = false } = {}) {
  const {
    retta_modalita: _rm,
    importo_retta_parziale: _irp,
    voci_retta_incluse: _vri,
    ...rest
  } = form;
  const payload = { mese, ...rest, ...buildRettaApiParams(form, { hasVociRettaConfig }) };
  delete payload.voci_retta_incluse;
  if (hasVociRettaConfig && Array.isArray(form.voci_retta_incluse)) {
    payload.voci_retta_incluse = form.voci_retta_incluse;
  }
  return payload;
}

function buildDeleteTarget(f, isFic) {
  return {
    id: f.id,
    numero: f.numero,
    provider_id: f.provider_id || f.fic_document_id,
    sdi_ei_status: f.sdi_ei_status,
    eliminaFic: fatturaEliminaSuFic(f, isFic),
  };
}

const STATO_FATTURA_BADGE = {
  bozza: { label: "Bozza", className: "bg-slate-100 text-slate-700" },
  generata: { label: "Generata", className: "bg-blue-100 text-blue-700" },
  approvata: { label: "Approvata", className: "bg-violet-100 text-violet-700" },
  in_coda_sdi: { label: "In coda SDI", className: "bg-sky-100 text-sky-800 ring-1 ring-sky-200" },
  inviata_sdi: { label: "Inviata SDI", className: "bg-amber-100 text-amber-700" },
  consegnata: { label: "Consegnata", className: "bg-emerald-100 text-emerald-700" },
  accettata: { label: "Accettata", className: "bg-emerald-100 text-emerald-700" },
  rifiutata: { label: "Rifiutata", className: "bg-red-100 text-red-700" },
  scartata: { label: "Scartata", className: "bg-red-100 text-red-700" },
  stornata: { label: "Stornata", className: "bg-purple-100 text-purple-700" },
};

function statoFatturaBadge(f) {
  if (fatturaInCodaSdi(f)) return STATO_FATTURA_BADGE.in_coda_sdi;
  return STATO_FATTURA_BADGE[f?.stato] || STATO_FATTURA_BADGE.bozza;
}

const STATO_PAGAMENTO_BADGE = {
  non_pagata: { label: "Non pagata", className: "bg-red-100 text-red-700" },
  parziale: { label: "Parziale", className: "bg-amber-100 text-amber-700" },
  pagata: { label: "Pagata", className: "bg-emerald-100 text-emerald-700" },
  scaduta: { label: "Scaduta", className: "bg-red-100 text-red-700" },
};

/** section: "fatturazione" | "storico" | "all" (default) */
export default function FatturazioneOspite({
  fascicoloId,
  amministrativa,
  anagrafica,
  fascicoloOrganizationId,
  section = "all",
}) {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [meseSelezionato, setMeseSelezionato] = useState(getMeseCorrente);
  const [anteprima, setAnteprima] = useState(null);
  const [fatture, setFatture] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingAnteprima, setLoadingAnteprima] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [sendingSdi, setSendingSdi] = useState(null);
  const [config, setConfig] = useState(null);

  const [showGeneraDialog, setShowGeneraDialog] = useState(false);
  const [generaForm, setGeneraForm] = useState({
    includi_retta: true,
    retta_modalita: "intera",
    importo_retta_parziale: "",
    voci_retta_incluse: [],
    includi_prima_nota: true,
    includi_deposito_cauzionale: false,
    destinatario_codice_fiscale: "",
    destinatario_partita_iva: "",
    destinatario_pec: "",
    destinatario_sdi: "",
    destinatario_email: "",
    destinatario_indirizzo: "",
    destinatario_cap: "",
    destinatario_citta: "",
    destinatario_provincia: "",
    modalita_pagamento: "MP05",
    scadenza_giorni: 30,
  });

  const [ficLookup, setFicLookup] = useState({ loading: false, items: null, error: null });

  const [showPagamentoDialog, setShowPagamentoDialog] = useState(false);
  const [pagamentoFatturaId, setPagamentoFatturaId] = useState(null);
  const [pagamentoForm, setPagamentoForm] = useState({
    importo: "", data_pagamento: new Date().toISOString().split("T")[0], metodo: "bonifico", note: "",
  });
  const [savingPagamento, setSavingPagamento] = useState(false);

  const [showSollecitoDialog, setShowSollecitoDialog] = useState(false);
  const [sollecitoFatturaId, setSollecitoFatturaId] = useState(null);
  const [sollecitoForm, setSollecitoForm] = useState({ messaggio: "", email_destinatario: "" });
  const [sendingSollecito, setSendingSollecito] = useState(false);

  const [expandedFattura, setExpandedFattura] = useState(null);
  const [showStoricoAll, setShowStoricoAll] = useState(false);
  const [savingFatturaPatch, setSavingFatturaPatch] = useState(false);
  const [deletingFattura, setDeletingFattura] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editVociDialogOpen, setEditVociDialogOpen] = useState(false);
  const [editVociTarget, setEditVociTarget] = useState(null);
  const [creatingNotaCredito, setCreatingNotaCredito] = useState(null);
  const [rigeneratingFattura, setRigeneratingFattura] = useState(null);

  const [previewPdfLoading, setPreviewPdfLoading] = useState(false);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [pdfPreviewTitle, setPdfPreviewTitle] = useState("Anteprima fattura PDF");
  const [fatturaPdfLoadingId, setFatturaPdfLoadingId] = useState(null);
  const [ficDialogOpen, setFicDialogOpen] = useState(false);
  const [ficSedeConfirm, setFicSedeConfirm] = useState(null);
  const [confirmingFicSede, setConfirmingFicSede] = useState(false);
  const [ficSedeGenera, setFicSedeGenera] = useState(null);
  const [salvandoSedeFic, setSalvandoSedeFic] = useState(false);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const downloadDeblisteringAllegato = useCallback(async (docId) => {
    try {
      const res = await axios.get(
        `${API_URL}/api/fatturazione/deblistering-documento/${docId}/download`,
        { headers, responseType: "blob" },
      );
      const hdrs = res.headers || {};
      const cd = hdrs["content-disposition"] || hdrs["Content-Disposition"] || "";
      let nameFromHeader = "";
      if (cd) {
        const star = /filename\*\s*=\s*UTF-8''([^;\s]+)/i.exec(cd);
        const quoted = /filename\s*=\s*"([^"]+)"/i.exec(cd);
        const plain = /filename\s*=\s*([^";\s]+)/i.exec(cd);
        if (star) {
          try {
            nameFromHeader = decodeURIComponent(star[1].trim());
          } catch {
            nameFromHeader = star[1].trim();
          }
        } else if (quoted) {
          nameFromHeader = quoted[1].trim();
        } else if (plain) {
          nameFromHeader = plain[1].trim();
        }
      }
      const blobType = String(res.data?.type || "")
        .split(";")[0]
        .trim()
        .toLowerCase();
      const headerCt = String(hdrs["content-type"] || hdrs["Content-Type"] || "")
        .split(";")[0]
        .trim()
        .toLowerCase();
      const ct = blobType || headerCt;
      const extForMime = (mime) =>
        mime === "image/png"
          ? ".png"
          : mime === "image/jpeg" || mime === "image/jpg"
            ? ".jpg"
            : mime === "image/webp"
              ? ".webp"
              : mime === "image/gif"
                ? ".gif"
                : mime === "application/pdf"
                  ? ".pdf"
                  : "";
      const ext = extForMime(ct) || ".bin";
      const baseFromName = nameFromHeader
        ? nameFromHeader.replace(/\.[^./\\]+$/, "").replace(/[/\\]/g, "_") || "allegato-fattura-farmacia"
        : "allegato-fattura-farmacia";
      const filename = `${baseFromName}${ext}`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossibile scaricare l'allegato");
    }
  }, [headers]);

  /** Allineato a _effective_org_id del backend: fascicolo, altrimenti struttura attiva utente (stesso fallback dell'anteprima). */
  const strutturaIdForConfig = useMemo(() => {
    if (fascicoloOrganizationId != null && String(fascicoloOrganizationId).trim() !== "") {
      return String(fascicoloOrganizationId);
    }
    return user?.struttura_id || user?.zohoRecordId || null;
  }, [fascicoloOrganizationId, user?.struttura_id, user?.zohoRecordId]);

  const configFetchGen = useRef(0);

  const loadConfig = useCallback(async () => {
    const gen = ++configFetchGen.current;
    try {
      const params = strutturaIdForConfig ? { strutturaId: strutturaIdForConfig } : {};
      const res = await axios.get(`${API_URL}/api/fatturazione/config`, { headers, params });
      if (gen !== configFetchGen.current) return;
      setConfig(res.data.config || {});
    } catch {
      if (gen !== configFetchGen.current) return;
      setConfig({});
    }
  }, [headers, strutturaIdForConfig]);

  const loadFatture = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/fatturazione/fatture/${fascicoloId}`, { headers });
      setFatture(res.data.fatture || []);
    } catch {
      /* silent - may not have fatture yet */
    } finally {
      if (!silent) setLoading(false);
    }
  }, [fascicoloId, headers]);

  const vociRettaConfig = useMemo(
    () => (Array.isArray(config?.voci_retta_mensile) ? config.voci_retta_mensile : []),
    [config?.voci_retta_mensile],
  );

  const anteprimaRettaParams = useMemo(() => {
    if (!showGeneraDialog) {
      // Preferenze fascicolo applicate lato server (default applica_preferenze_retta=true).
      return {};
    }
    return buildRettaApiParams(generaForm, { hasVociRettaConfig: vociRettaConfig.length > 0 });
  }, [showGeneraDialog, generaForm, vociRettaConfig.length]);

  const loadAnteprima = useCallback(async () => {
    setLoadingAnteprima(true);
    try {
      const res = await axios.get(`${API_URL}/api/fatturazione/anteprima/${fascicoloId}`, {
        headers,
        params: {
          mese: meseSelezionato,
          includi_deposito_cauzionale: showGeneraDialog ? generaForm.includi_deposito_cauzionale : false,
          ...anteprimaRettaParams,
        },
      });
      setAnteprima(res.data);
    } catch {
      setAnteprima(null);
    } finally {
      setLoadingAnteprima(false);
    }
  }, [
    fascicoloId,
    headers,
    meseSelezionato,
    showGeneraDialog,
    generaForm.includi_deposito_cauzionale,
    anteprimaRettaParams,
  ]);

  const openPdfPreview = useCallback((blob, title = "Anteprima fattura PDF") => {
    setPdfPreviewTitle(title);
    setPdfPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(blob);
    });
    setPdfPreviewOpen(true);
  }, []);

  const handlePdfPreviewDialogChange = useCallback((open) => {
    setPdfPreviewOpen(open);
    if (!open) {
      setPdfPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    }
  }, []);

  const handleAnteprimaPdf = useCallback(async () => {
    if (!anteprima?.voci?.length) {
      toast.error("Nessuna voce disponibile per l'anteprima PDF di questo mese");
      return;
    }
    setPreviewPdfLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/fatturazione/anteprima/${fascicoloId}/pdf`,
        {
          headers,
          params: {
            mese: meseSelezionato,
            includi_deposito_cauzionale: showGeneraDialog ? generaForm.includi_deposito_cauzionale : false,
            ...anteprimaRettaParams,
          },
          responseType: "blob",
        },
      );
      openPdfPreview(res.data, `Anteprima fattura — ${formatMese(meseSelezionato)}`);
    } catch (e) {
      let msg = "Impossibile generare l'anteprima PDF";
      const data = e.response?.data;
      if (data instanceof Blob) {
        try {
          const t = await data.text();
          const j = JSON.parse(t);
          if (j.detail) msg = typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail);
        } catch {
          /* ignore */
        }
      } else if (e.response?.data?.detail) {
        msg = e.response.data.detail;
      }
      toast.error(msg);
    } finally {
      setPreviewPdfLoading(false);
    }
  }, [
    fascicoloId,
    headers,
    meseSelezionato,
    showGeneraDialog,
    generaForm.includi_deposito_cauzionale,
    anteprimaRettaParams,
    anteprima,
    openPdfPreview,
  ]);

  const handleFatturaPdf = useCallback(
    async (f) => {
      if (!f?.id) return;
      setFatturaPdfLoadingId(f.id);
      try {
        const res = await axios.get(`${API_URL}/api/fatturazione/fattura/${f.id}/pdf`, {
          headers,
          responseType: "blob",
        });
        openPdfPreview(res.data, f.numero ? `Fattura ${f.numero}` : "Fattura PDF");
      } catch (e) {
        let msg = "Impossibile aprire il PDF della fattura";
        const data = e.response?.data;
        if (data instanceof Blob) {
          try {
            const t = await data.text();
            const j = JSON.parse(t);
            if (j.detail) msg = typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail);
          } catch {
            /* ignore */
          }
        } else if (e.response?.data?.detail) {
          msg = e.response.data.detail;
        }
        toast.error(msg);
      } finally {
        setFatturaPdfLoadingId(null);
      }
    },
    [headers, openPdfPreview],
  );

  const handleFatturaPdfFic = useCallback(
    async (f) => {
      if (!f?.id) return;
      setFatturaPdfLoadingId(`fic-${f.id}`);
      try {
        const res = await axios.get(`${API_URL}/api/fatturazione/fattura/${f.id}/pdf-fic`, {
          headers,
          responseType: "blob",
        });
        openPdfPreview(res.data, f.numero ? `Fattura ${f.numero} (FIC)` : "Fattura PDF (FIC)");
      } catch (e) {
        toast.error("Impossibile aprire il PDF da Fatture in Cloud");
      } finally {
        setFatturaPdfLoadingId(null);
      }
    },
    [headers, openPdfPreview],
  );

  useEffect(() => {
    return () => {
      if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
    };
  }, [pdfPreviewUrl]);

  useEffect(() => { loadConfig(); }, [loadConfig]);
  useEffect(() => { loadFatture(); }, [loadFatture]);
  useEffect(() => { loadAnteprima(); }, [loadAnteprima]);

  useEffect(() => {
    if (anagrafica) {
      const destCustom = destinatarioPerGeneraFattura(amministrativa, anagrafica);
      if (destCustom) {
        setGeneraForm((prev) => {
          const changed = Object.entries(destCustom).some(([key, value]) => prev[key] !== value);
          return changed ? { ...prev, ...destCustom } : prev;
        });
        return;
      }

      const fam = amministrativa?.familiare || {};
      const emailContatto =
        (anagrafica.contatti_familiari || []).map((c) => c?.email).find(Boolean) ||
        fam.email ||
        anagrafica.email ||
        "";
      const sede = parseIndirizzoResidenza(anagrafica.indirizzo_residenza);
      setGeneraForm(prev => {
        const nextDefaults = {
          destinatario_codice_fiscale: anagrafica.codice_fiscale || prev.destinatario_codice_fiscale,
          destinatario_email: emailContatto || prev.destinatario_email,
          destinatario_indirizzo: anagrafica.indirizzo || sede.indirizzo || prev.destinatario_indirizzo,
          destinatario_cap: anagrafica.cap || sede.cap || prev.destinatario_cap,
          destinatario_citta: anagrafica.citta || anagrafica.comune || sede.citta || prev.destinatario_citta,
          destinatario_provincia: anagrafica.provincia || sede.provincia || prev.destinatario_provincia,
          destinatario_pec: fam.pec || amministrativa?.pec || prev.destinatario_pec,
          destinatario_sdi: normalizeCodiceSdi(
            fam.codice_sdi || amministrativa?.codice_sdi || prev.destinatario_sdi,
          ),
        };
        const changed = Object.entries(nextDefaults).some(([key, value]) => prev[key] !== value);
        return changed ? { ...prev, ...nextDefaults } : prev;
      });
    }
  }, [anagrafica, amministrativa]);

  const handleGenera = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/fatturazione/fatture/${fascicoloId}/genera`,
        buildGeneraPayload(generaForm, meseSelezionato, { hasVociRettaConfig: vociRettaConfig.length > 0 }),
        { headers },
      );
      toast.success(res.data.message || "Fattura generata");
      setShowGeneraDialog(false);
      await loadFatture();
      await loadAnteprima();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nella generazione");
    } finally {
      setGenerating(false);
    }
  };

  const handleInviaSdi = async (fatturaId, { applicaSedeFic = false } = {}) => {
    if (!applicaSedeFic) setSendingSdi(fatturaId);
    else setConfirmingFicSede(true);
    try {
      const res = await postInviaSdi(axios, API_URL, fatturaId, {
        headers,
        strutturaId: strutturaIdForConfig,
        applicaSedeFic,
      });
      if (res.data.success) {
        setFicSedeConfirm(null);
        toast.success(res.data.message || "Fattura inviata a SDI");
      } else {
        toastSdiError(res.data?.message_technical || res.data?.message, {
          userMessage: res.data?.message,
        });
      }
      setFatture((prev) =>
        prev.map((f) => {
          if (f.id !== fatturaId) return f;
          const next = { ...f };
          if (res.data.stato) next.stato = res.data.stato;
          if (res.data.success) {
            if (!next.sdi_invio_at) next.sdi_invio_at = new Date().toISOString();
            next.ultimo_errore_sdi = undefined;
            next.ultimo_errore_sdi_leggibile = undefined;
          } else if (res.data.message) {
            next.ultimo_errore_sdi_leggibile = res.data.message;
            if (res.data.message_technical) next.ultimo_errore_sdi = res.data.message_technical;
          }
          return next;
        }),
      );
      await loadFatture({ silent: true });
    } catch (err) {
      if (!applicaSedeFic && isFicSedePropostaError(err)) {
        const proposta = ficSedePropostaFromError(err);
        if (proposta) {
          setFicSedeConfirm(proposta);
          return;
        }
      }
      const d = err.response?.data;
      toastSdiError(d?.message_technical || d?.detail || d?.message, {
        userMessage: d?.message || (typeof d?.detail === "string" ? d.detail : undefined),
      });
      await loadFatture();
    } finally {
      if (!applicaSedeFic) setSendingSdi(null);
      else setConfirmingFicSede(false);
    }
  };

  const handleConfermaSedeFicInvio = () => {
    if (!ficSedeConfirm?.fatturaId) return;
    handleInviaSdi(ficSedeConfirm.fatturaId, { applicaSedeFic: true });
  };

  const handleDownloadXml = async (fatturaId, numero) => {
    try {
      const res = await axios.get(`${API_URL}/api/fatturazione/fatture/${fatturaId}/xml`, {
        headers, responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${numero.replace(/\//g, "_")}.xml`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Errore download XML");
    }
  };

  const handleRicalcolaFatturaSenzaBollo = async (fatturaId) => {
    setSavingFatturaPatch(true);
    try {
      await axios.patch(`${API_URL}/api/fatturazione/fattura/${fatturaId}`, {}, { headers });
      toast.success("Fattura aggiornata (totali e XML)");
      await loadFatture();
      await loadAnteprima();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Aggiornamento non riuscito");
    } finally {
      setSavingFatturaPatch(false);
    }
  };

  const openEditVociDialog = (f) => {
    if (!f?.voci?.length) {
      toast.error("Nessuna voce da modificare");
      return;
    }
    setEditVociTarget({ id: f.id, voci: f.voci });
    setEditVociDialogOpen(true);
  };

  const handleSaveVociFromEditor = async (voci) => {
    const fid = editVociTarget?.id;
    if (!fid) return;
    setSavingFatturaPatch(true);
    try {
      await axios.patch(`${API_URL}/api/fatturazione/fattura/${fid}`, { voci }, { headers });
      toast.success("Voci aggiornate");
      setEditVociDialogOpen(false);
      setEditVociTarget(null);
      await loadFatture();
      await loadAnteprima();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Salvataggio non riuscito");
    } finally {
      setSavingFatturaPatch(false);
    }
  };

  const confirmDeleteFattura = async () => {
    if (!deleteTarget) return;
    setDeletingFattura(true);
    try {
      const params = {};
      if (strutturaIdForConfig) params.strutturaId = strutturaIdForConfig;
      if (deleteTarget.eliminaFic) params.elimina_fic = true;
      const res = await axios.delete(`${API_URL}/api/fatturazione/fattura/${deleteTarget.id}`, {
        headers,
        params,
      });
      toast.success(res.data?.message || "Fattura eliminata");
      setDeleteTarget(null);
      setExpandedFattura(null);
      await loadFatture();
      await loadAnteprima();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eliminazione non riuscita");
    } finally {
      setDeletingFattura(false);
    }
  };

  const handleCreaNotaCredito = async (f) => {
    if (!f?.id) return;
    setCreatingNotaCredito(f.id);
    try {
      const res = await axios.post(`${API_URL}/api/fatturazione/fatture/${f.id}/nota-credito`, {}, { headers });
      toast.success(res.data?.message || "Nota di credito creata");
      await loadFatture();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Creazione nota di credito non riuscita");
    } finally {
      setCreatingNotaCredito(null);
    }
  };

  const handleRigeneraFattura = async (f) => {
    if (!f?.id) return;
    setRigeneratingFattura(f.id);
    try {
      const res = await axios.post(`${API_URL}/api/fatturazione/fatture/${f.id}/rigenera`, {}, { headers });
      toast.success(res.data?.message || "Fattura rigenerata");
      await loadFatture();
      await loadAnteprima();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rigenerazione non riuscita");
    } finally {
      setRigeneratingFattura(null);
    }
  };

  const handleScollegaFic = async (f) => {
    if (!f?.id) return;
    if (
      !window.confirm(
        `Scollegare ${f.numero || "questa fattura"} da Fatture in Cloud?\n` +
          "Il documento Affidya resta; puoi collegarne un altro o rigenerare la fattura del mese.",
      )
    ) {
      return;
    }
    try {
      const res = await axios.post(
        `${API_URL}/api/fatturazione/fatture/${f.id}/scollega-fic`,
        {},
        { headers },
      );
      toast.success(res.data?.message || "Collegamento FIC rimosso");
      await loadFatture();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Scollegamento FIC non riuscito");
    }
  };

  const handleRegistraPagamento = async () => {
    if (!pagamentoForm.importo || parseFloat(pagamentoForm.importo) <= 0) {
      return toast.error("Inserisci un importo valido");
    }
    setSavingPagamento(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/fatturazione/fatture/${pagamentoFatturaId}/pagamento`,
        { ...pagamentoForm, importo: parseFloat(pagamentoForm.importo) },
        { headers },
      );
      toast.success(res.data.message || "Pagamento registrato");
      setShowPagamentoDialog(false);
      setPagamentoForm({ importo: "", data_pagamento: new Date().toISOString().split("T")[0], metodo: "bonifico", note: "" });
      await loadFatture();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore registrazione pagamento");
    } finally {
      setSavingPagamento(false);
    }
  };

  const handleInviaSollecito = async () => {
    setSendingSollecito(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/fatturazione/fatture/${sollecitoFatturaId}/sollecito`,
        sollecitoForm,
        { headers },
      );
      toast.success(res.data.message || "Sollecito inviato");
      setShowSollecitoDialog(false);
      setSollecitoForm({ messaggio: "", email_destinatario: "" });
      await loadFatture();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore invio sollecito");
    } finally {
      setSendingSollecito(false);
    }
  };

  /** Tutte le fatture del mese selezionato (stesso ospite può avere più documenti: retta + extra). */
  const fattureDelMese = useMemo(() => {
    return fatture
      .filter(f => f.mese === meseSelezionato)
      .sort((a, b) => {
        const da = a.data_emissione ? new Date(a.data_emissione).getTime() : 0;
        const db_ = b.data_emissione ? new Date(b.data_emissione).getTime() : 0;
        return db_ - da;
      });
  }, [fatture, meseSelezionato]);

  const rettaResidua = anteprima?.retta_residua ?? null;
  const rettaGiaFatturata = anteprima?.retta_gia_fatturata ?? 0;

  const rettaCompletamenteFatturata = useMemo(() => {
    if (rettaResidua != null) return rettaResidua <= 0;
    return fattureDelMese.some(
      f =>
        !["rifiutata", "scartata"].includes(f.stato) &&
        (f.voci || []).some(v => v.tipo === "retta" || v.tipo === "retta_voce"),
    );
  }, [rettaResidua, fattureDelMese]);

  const rettaGiaFatturataNelMese = rettaCompletamenteFatturata;

  const fatturaConDeposito = useMemo(
    () =>
      fatture.find(
        f =>
          !["rifiutata", "scartata"].includes(f.stato) &&
          (f.voci || []).some(v => v.tipo === "deposito_cauzionale"),
      ),
    [fatture],
  );

  const depositoGiaFatturato = !!fatturaConDeposito;

  const depImportoConfigured = Number(amministrativa?.deposito_cauzionale) > 0
    && amministrativa?.deposito_cauzionale_abilitato !== false;

  const canGeneraFattura =
    (anteprima?.voci?.length > 0) || (depImportoConfigured && !depositoGiaFatturato);

  const openGeneraDialog = () => {
    const residua =
      anteprima?.retta_residua ??
      Math.max(0, Number(amministrativa?.retta_mensile || 0) - rettaGiaFatturata);
    const prefsRetta = preferenzeToGeneraFormRetta(amministrativa?.fatturazione_retta, {
      rettaResidua: residua,
      vociRettaConfig,
    });
    setGeneraForm(prev => ({
      ...prev,
      ...prefsRetta,
      includi_prima_nota: true,
      includi_deposito_cauzionale: false,
    }));
    setFicLookup({ loading: false, items: null, error: null });
    setFicSedeGenera(null);
    setShowGeneraDialog(true);
    loadFicSedeGenera();
  };

  const loadFicSedeGenera = useCallback(async () => {
    if (!fascicoloId || config?.provider !== "fatture_in_cloud") return;
    try {
      const params = strutturaIdForConfig ? { strutturaId: strutturaIdForConfig } : {};
      const res = await axios.get(
        `${API_URL}/api/fatturazione/fascicolo/${fascicoloId}/sede-da-fic`,
        { headers, params },
      );
      if (res.data?.disponibile && res.data.fic_sede) {
        setFicSedeGenera(res.data.fic_sede);
      }
    } catch {
      setFicSedeGenera(null);
    }
  }, [fascicoloId, config?.provider, headers, strutturaIdForConfig]);

  const handleApplicaSedeFicGenera = async () => {
    if (!fascicoloId) return;
    setSalvandoSedeFic(true);
    try {
      const params = strutturaIdForConfig ? { strutturaId: strutturaIdForConfig } : {};
      const res = await axios.post(
        `${API_URL}/api/fatturazione/fascicolo/${fascicoloId}/applica-sede-fic`,
        {},
        { headers, params },
      );
      const sede = res.data?.sede || ficSedeGenera;
      if (sede) {
        setGeneraForm(prev => ({
          ...prev,
          destinatario_indirizzo: sede.indirizzo || prev.destinatario_indirizzo,
          destinatario_cap: sede.cap || prev.destinatario_cap,
          destinatario_citta: sede.citta || prev.destinatario_citta,
          destinatario_provincia: sede.provincia || prev.destinatario_provincia,
        }));
      }
      setFicSedeGenera(null);
      toast.success(res.data?.message || "Indirizzo importato da FIC e salvato in anagrafica");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Importazione indirizzo FIC non riuscita");
    } finally {
      setSalvandoSedeFic(false);
    }
  };

  const handleFicLookup = async (cf) => {
    if (!cf || !cf.trim()) return;
    setFicLookup({ loading: true, items: null, error: null });
    try {
      const params = { cf: cf.trim() };
      if (strutturaIdForConfig) params.strutturaId = strutturaIdForConfig;
      const res = await axios.get(`${API_URL}/api/fatturazione/cerca-anagrafica`, { headers, params });
      if (res.data.success === false) {
        setFicLookup({ loading: false, items: [], error: res.data.message || "Nessun risultato" });
      } else {
        setFicLookup({ loading: false, items: res.data.items || [], error: null });
      }
    } catch (e) {
      setFicLookup({ loading: false, items: [], error: e.response?.data?.detail || "Errore ricerca" });
    }
  };

  const applyFicClient = async (client) => {
    const sdiValue = (() => {
      const n = normalizeCodiceSdi(client.codice_sdi);
      return n !== "0000000" ? n : undefined;
    })();
    setGeneraForm(prev => ({
      ...prev,
      destinatario_partita_iva: client.partita_iva || prev.destinatario_partita_iva,
      destinatario_pec: client.pec || prev.destinatario_pec,
      ...(sdiValue !== undefined && { destinatario_sdi: sdiValue }),
      destinatario_indirizzo: client.indirizzo || prev.destinatario_indirizzo,
      destinatario_cap: client.cap || prev.destinatario_cap,
      destinatario_citta: client.citta || prev.destinatario_citta,
      destinatario_provincia: client.provincia || prev.destinatario_provincia,
      destinatario_email: client.email || prev.destinatario_email,
    }));
    setFicLookup(prev => ({ ...prev, items: null }));

    if (client.indirizzo && client.cap && client.citta && client.provincia && fascicoloId) {
      try {
        await axios.post(
          `${API_URL}/api/fatturazione/fascicolo/${fascicoloId}/salva-sede-anagrafica`,
          {
            indirizzo: client.indirizzo,
            cap: client.cap,
            citta: client.citta,
            provincia: client.provincia,
            fonte: "fatture_in_cloud",
          },
          { headers },
        );
        toast.success("Dati FIC applicati e salvati in anagrafica ospite");
      } catch (e) {
        toast.warning("Dati applicati al form; salvataggio in anagrafica non riuscito");
      }
    } else {
      toast.success("Dati anagrafica FIC applicati");
    }
  };

  const hasProviderConfig = !!(config && config.provider);
  const canInviaSdi = hasProviderConfig && !!config.abilitato;
  const isFic = config?.provider === "fatture_in_cloud";
  const showFatturazione = section === "all" || section === "fatturazione";
  const showStorico = section === "all" || section === "storico";
  const displayFatture =
    section === "storico" || showStoricoAll ? fatture : fatture.slice(0, 5);

  return (
    <div className={section === "all" ? "space-y-6 mt-6" : "space-y-6"}>
      {showFatturazione && (
      <>
      {/* Header sezione */}
      <Card className="card-base">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Receipt className="w-5 h-5 text-emerald-600" />
              Fatturazione Ospite
            </CardTitle>
            <div className="flex items-center gap-2">
              <Select value={meseSelezionato} onValueChange={setMeseSelezionato}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 12 }, (_, i) => {
                    const d = new Date();
                    d.setMonth(d.getMonth() - i);
                    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
                    return <SelectItem key={val} value={val}>{formatMese(val)}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
              <Button variant="outline" size="icon" onClick={loadAnteprima} disabled={loadingAnteprima}>
                <RefreshCw className={`w-4 h-4 ${loadingAnteprima ? "animate-spin" : ""}`} />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1.5 shrink-0 inline-flex"
                onClick={handleAnteprimaPdf}
                disabled={loadingAnteprima || previewPdfLoading || !(anteprima?.voci?.length > 0)}
                title="Apri anteprima PDF nel browser"
              >
                {previewPdfLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
                Anteprima PDF
              </Button>
            </div>
          </div>
          {!hasProviderConfig && (
            <div className="mt-2 flex items-center gap-2 text-amber-600 bg-amber-50 p-2 rounded-lg text-sm">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span>
                Fatturazione elettronica non configurata.
                <a href="/impostazioni" className="underline ml-1 font-medium">Vai alle impostazioni</a>
              </span>
            </div>
          )}
          {hasProviderConfig && !config.abilitato && (
            <div className="mt-2 flex items-center gap-2 text-slate-600 bg-slate-100 p-2 rounded-lg text-sm">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>
                Provider e dati cedente sono impostati, ma la fatturazione non è abilitata.
                Attiva l&apos;opzione in{" "}
                <a href="/impostazioni" className="underline font-medium">Impostazioni</a>
                {" "}per inviare a SDI.
              </span>
            </div>
          )}
        </CardHeader>
      </Card>

      {/* Riepilogo Retta */}
      <Card className="card-base">
        <CardContent className="pt-6">
          <div className="bg-white rounded-lg p-4 border border-slate-200">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold text-slate-700 flex items-center gap-2">
                <Euro className="w-4 h-4 text-emerald-500" />
                Retta Mensile (Soggiorno)
              </h4>
              <div className="text-right">
                <span className="text-2xl font-bold text-emerald-600">
                  € {(amministrativa?.retta_mensile || 0).toLocaleString("it-IT", { minimumFractionDigits: 2 })}
                </span>
                <p className="text-xs text-slate-500">Esente IVA Art. 10 DPR 633/72</p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div className="bg-slate-50 p-2 rounded">
                <span className="text-slate-500">Ente Pagante</span>
                <p className="font-medium">{amministrativa?.ente_pagante || "Privato"}</p>
              </div>
              <div className="bg-slate-50 p-2 rounded">
                <span className="text-slate-500">Tipo Contratto</span>
                <p className="font-medium">{amministrativa?.tipo_contratto || "N/D"}</p>
              </div>
              <div className="bg-slate-50 p-2 rounded">
                <span className="text-slate-500">Frequenza</span>
                <p className="font-medium">Mensile</p>
              </div>
              <div className="bg-slate-50 p-2 rounded">
                <span className="text-slate-500">Modalità</span>
                <p className="font-medium">Bonifico</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Anteprima fattura */}
      {loadingAnteprima ? (
        <Card className="card-base">
          <CardContent className="py-12 text-center">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
            <p className="text-sm text-slate-500 mt-2">Caricamento anteprima...</p>
          </CardContent>
        </Card>
      ) : anteprima ? (
        <Card className="card-base border-emerald-200 bg-gradient-to-r from-emerald-50/50 to-teal-50/50">
          <CardHeader className="pb-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <CardTitle className="flex items-center gap-2 text-base">
                <FileText className="w-4 h-4 text-emerald-600" />
                Anteprima Fattura - {formatMese(meseSelezionato)}
              </CardTitle>
              <div className="flex flex-wrap items-center gap-2 justify-start sm:justify-end">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="gap-1.5 border-emerald-300 text-emerald-800 bg-white hover:bg-emerald-50"
                  onClick={handleAnteprimaPdf}
                  disabled={loadingAnteprima || previewPdfLoading || !(anteprima?.voci?.length > 0)}
                  title={
                    !(anteprima?.voci?.length > 0)
                      ? "Nessuna voce per questo mese in anteprima (già tutto in fattura). Usa il pulsante PDF sulla fattura emessa nello storico."
                      : "Apri anteprima PDF nel browser"
                  }
                >
                  {previewPdfLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                  Anteprima PDF
                </Button>
                {fattureDelMese.length > 0 && (
                  <div className="flex flex-wrap gap-1 max-w-[220px]">
                    {fattureDelMese.map(f => (
                      <Badge
                        key={f.id}
                        className={STATO_FATTURA_BADGE[f.stato]?.className}
                        title={f.numero}
                      >
                        {f.numero?.split("-").pop() || f.numero}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {anteprima.avvisi?.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50/80 px-3 py-2 text-sm text-amber-900">
                {anteprima.avvisi.map((a, i) => (
                  <p key={i}>{a}</p>
                ))}
              </div>
            )}
            <p className="text-xs text-slate-500">
              Puoi emettere più fatture nello stesso mese (es. retta parziale e, in seguito, il residuo o le spese extra da Prima Nota).
              Le voci già incluse in una fattura attiva non vengono ripetute.
            </p>
            {anteprima && !(anteprima.voci?.length > 0) && fattureDelMese.length > 0 && (
              <p className="text-xs text-amber-900/90 bg-amber-50/90 border border-amber-200/80 rounded-lg px-3 py-2">
                Per questo mese non restano voci in anteprima (sono già in una fattura emessa). Per vedere il PDF apri la
                fattura {section === "fatturazione" ? "nel tab Storico fatture" : "nello storico qui sotto"} e usa il pulsante{" "}
                <strong>PDF</strong>.
              </p>
            )}
            {/* Voci fattura */}
            <div className="bg-white/70 rounded-lg p-4 border border-slate-200">
              <h4 className="font-semibold text-slate-700 flex items-center gap-2 mb-3">
                <ClipboardList className="w-4 h-4 text-slate-500" />
                Voci Fattura
              </h4>
              <div className="space-y-2">
                {anteprima.voci.map((voce, idx) => {
                  const regime = REGIMI_IVA_LABELS[voce.regime_iva] || voce.regime_iva;
                  const debAtt =
                    voce.tipo === "deblistering_fattura_farmacia" && voce.deblistering_documento_id;
                  return (
                    <div key={idx} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
                        {debAtt ? (
                          <>
                            <span className="text-sm text-slate-800">Fattura farmacia (deblistering)</span>
                            <button
                              type="button"
                              onClick={() => downloadDeblisteringAllegato(voce.deblistering_documento_id)}
                              className="inline-flex items-center gap-1 text-sm font-medium text-cyan-700 hover:text-cyan-900"
                            >
                              <Paperclip className="h-3.5 w-3.5 shrink-0" />
                              Scarica DDT / allegato
                            </button>
                          </>
                        ) : (
                          <>
                            <span className="text-sm">{voceDescrizionePerUI(voce)}</span>
                            {voce.quantita > 1 && <span className="text-xs text-slate-400">x{voce.quantita}</span>}
                          </>
                        )}
                      </div>
                      <div className="text-right">
                        <span className="font-medium text-slate-700">
                          € {(voce.quantita * voce.prezzo_unitario).toLocaleString("it-IT", { minimumFractionDigits: 2 })}
                        </span>
                        <p className="text-[10px] text-slate-400">{regime}</p>
                      </div>
                    </div>
                  );
                })}
                {anteprima.voci.length === 0 && (
                  <p className="text-sm text-slate-400 text-center py-4">
                    Nessuna voce per questo mese
                    {depImportoConfigured && !depositoGiaFatturato && (
                      <span className="block mt-2 text-amber-900/90 text-xs leading-snug">
                        È presente un deposito cauzionale in anagrafica: apri &quot;Genera fattura&quot; e seleziona
                        l&apos;inclusione del deposito per questo documento.
                      </span>
                    )}
                  </p>
                )}
              </div>
            </div>

            {/* Riepilogo IVA */}
            {anteprima.riepilogo_iva.length > 0 && (
              <div className="bg-white/70 rounded p-3 text-sm">
                <p className="font-semibold text-slate-700 mb-2">Riepilogo IVA</p>
                <div className="grid grid-cols-4 gap-2 text-xs text-slate-500 border-b pb-1 mb-1">
                  <span>Aliquota</span>
                  <span className="text-right">Imponibile</span>
                  <span className="text-right">Imposta</span>
                  <span className="text-right">Totale</span>
                </div>
                {anteprima.riepilogo_iva.map((r, i) => (
                  <div key={i} className="grid grid-cols-4 gap-2 text-sm py-1 border-b border-slate-100 last:border-0">
                    <span className="text-slate-600">{r.label}</span>
                    <span className="text-right">€ {r.imponibile.toLocaleString("it-IT", { minimumFractionDigits: 2 })}</span>
                    <span className="text-right text-amber-600">{r.imposta > 0 ? `€ ${r.imposta.toLocaleString("it-IT", { minimumFractionDigits: 2 })}` : "-"}</span>
                    <span className="text-right font-medium">€ {r.totale.toLocaleString("it-IT", { minimumFractionDigits: 2 })}</span>
                  </div>
                ))}
                {anteprima.bollo > 0 && (
                  <div className="grid grid-cols-4 gap-2 text-sm py-1">
                    <span className="text-slate-600">Bollo</span>
                    <span className="text-right">-</span>
                    <span className="text-right">-</span>
                    <span className="text-right font-medium">€ {anteprima.bollo.toLocaleString("it-IT", { minimumFractionDigits: 2 })}</span>
                  </div>
                )}
              </div>
            )}

            {/* Totale */}
            <div className="flex justify-between pt-3 border-t-2 border-emerald-300 font-bold text-lg">
              <span>TOTALE FATTURA</span>
              <span className="text-emerald-700">
                € {(anteprima.totale || 0).toLocaleString("it-IT", { minimumFractionDigits: 2 })}
              </span>
            </div>

            {/* Azioni */}
            <div className="space-y-3 pt-2">
              <Button
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                onClick={openGeneraDialog}
                disabled={!canGeneraFattura}
              >
                <Plus className="w-4 h-4 mr-2" />
                {fattureDelMese.length ? "Genera altra fattura" : "Genera fattura"}
              </Button>
              {isFic && fattureDelMese.length === 0 && (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full border-sky-200 text-sky-800 hover:bg-sky-50"
                  onClick={() => setFicDialogOpen(true)}
                >
                  <Link2 className="w-4 h-4 mr-2" />
                  Collega fattura da FIC
                </Button>
              )}
              <Button
                variant="outline"
                className="w-full text-xs"
                onClick={() => navigate(`/fatturazione/nuova?fascicoloId=${fascicoloId}`)}
              >
                <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                Apri editor avanzato
              </Button>
              {fattureDelMese.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-slate-600">Fatture emesse per questo mese</p>
                  {fattureDelMese.map(f => (
                    <div
                      key={f.id}
                      className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white/80 px-3 py-2"
                    >
                      <span className="text-sm font-medium flex-1 min-w-[120px]">{f.numero}</span>
                      {f.tipo_documento === "nota_credito" && (
                        <Badge className="text-[10px] bg-purple-100 text-purple-800">NC</Badge>
                      )}
                      <Badge
                        className={`${statoFatturaBadge(f).className} text-[10px] inline-flex items-center gap-1`}
                        title={fatturaInCodaSdi(f) ? "Invio allo SDI in corso in background" : undefined}
                      >
                        {fatturaInCodaSdi(f) && <Loader2 className="w-3 h-3 animate-spin" aria-hidden />}
                        {statoFatturaBadge(f).label}
                      </Badge>
                      {f.nota_credito_numero && f.tipo_documento !== "nota_credito" && (
                        <Badge className="text-[10px] bg-purple-50 text-purple-800">
                          NC {f.nota_credito_numero}
                        </Badge>
                      )}
                      {f.fattura_riferimento_numero && f.tipo_documento === "nota_credito" && (
                        <Badge className="text-[10px] bg-slate-100 text-slate-700">
                          Rif. {f.fattura_riferimento_numero}
                        </Badge>
                      )}
                      {f.origine_documento === "fic_collegata" && (
                        <Badge className="text-[10px] bg-sky-100 text-sky-800">FIC</Badge>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 border-violet-200 text-violet-800"
                        onClick={() =>
                          f.origine_documento === "fic_collegata"
                            ? handleFatturaPdfFic(f)
                            : handleFatturaPdf(f)
                        }
                        disabled={fatturaPdfLoadingId === f.id || fatturaPdfLoadingId === `fic-${f.id}`}
                        title={f.origine_documento === "fic_collegata" ? "PDF da Fatture in Cloud" : "PDF Affidya"}
                      >
                        {fatturaPdfLoadingId === f.id || fatturaPdfLoadingId === `fic-${f.id}` ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Eye className="w-3 h-3" />
                        )}
                      </Button>
                      {["generata", "approvata", "rifiutata", "scartata"].includes(f.stato) &&
                        !fatturaInCodaSdi(f) && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 border-emerald-300 text-emerald-700"
                          onClick={() => handleInviaSdi(f.id)}
                          disabled={sendingSdi === f.id || !canInviaSdi}
                        >
                          {sendingSdi === f.id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <Send className="w-3 h-3" />
                          )}
                        </Button>
                      )}
                      {f.tipo_documento !== "nota_credito" &&
                        ["inviata_sdi", "consegnata", "accettata"].includes(f.stato) &&
                        !f.nota_credito_id && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 border-purple-200 text-purple-800"
                            onClick={() => handleCreaNotaCredito(f)}
                            disabled={creatingNotaCredito === f.id}
                            title="Crea nota di credito di storno totale"
                          >
                            {creatingNotaCredito === f.id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <FileText className="w-3 h-3" />
                            )}
                          </Button>
                        )}
                      {f.tipo_documento !== "nota_credito" && f.stato === "stornata" && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 border-blue-200 text-blue-800"
                          onClick={() => handleRigeneraFattura(f)}
                          disabled={rigeneratingFattura === f.id}
                          title="Rigenera una nuova bozza dalla fattura stornata"
                        >
                          {rigeneratingFattura === f.id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <RefreshCw className="w-3 h-3" />
                          )}
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8"
                        onClick={() => handleDownloadXml(f.id, f.numero)}
                        title="Scarica XML"
                      >
                        <Download className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ) : null}
      </>
      )}

      {showStorico && (
      <Card className="card-base">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <History className="w-4 h-4 text-slate-500" />
            Storico Fatture
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-6">
              <Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" />
            </div>
          ) : fatture.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">Nessuna fattura emessa</p>
          ) : (
            <div className="space-y-2">
              {displayFatture.map((f) => {
                const statoBadge = statoFatturaBadge(f);
                const pagBadge = STATO_PAGAMENTO_BADGE[f.stato_pagamento] || STATO_PAGAMENTO_BADGE.non_pagata;
                const isExpanded = expandedFattura === f.id;
                const inCodaSdi = fatturaInCodaSdi(f);

                return (
                  <div key={f.id} className="bg-slate-50 rounded-lg overflow-hidden">
                    <div
                      className="flex items-center justify-between py-3 px-4 cursor-pointer hover:bg-slate-100 transition-colors"
                      onClick={() => setExpandedFattura(isExpanded ? null : f.id)}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          f.stato_pagamento === "pagata" ? "bg-emerald-100" :
                          f.stato_pagamento === "scaduta" ? "bg-red-100" : "bg-amber-100"
                        }`}>
                          {f.stato_pagamento === "pagata" ? <Check className="w-4 h-4 text-emerald-600" /> :
                           f.stato_pagamento === "scaduta" ? <AlertCircle className="w-4 h-4 text-red-600" /> :
                           <Clock className="w-4 h-4 text-amber-600" />}
                        </div>
                        <div>
                          <p className="font-medium text-sm">{f.numero}</p>
                          <p className="text-xs text-slate-500">{formatMese(f.mese)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="font-semibold">€ {(f.totale || 0).toLocaleString("it-IT", { minimumFractionDigits: 2 })}</p>
                          <div className="flex gap-1">
                            {f.tipo_documento === "nota_credito" && (
                              <Badge className="bg-purple-100 text-purple-800 text-[10px]">NC</Badge>
                            )}
                            <Badge
                              className={`${statoBadge.className} text-[10px] inline-flex items-center gap-1`}
                              title={inCodaSdi ? "Invio allo SDI in corso in background" : undefined}
                            >
                              {inCodaSdi && <Loader2 className="w-3 h-3 animate-spin" aria-hidden />}
                              {statoBadge.label}
                            </Badge>
                            <Badge className={`${pagBadge.className} text-[10px]`}>{pagBadge.label}</Badge>
                          </div>
                        </div>
                        {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="px-4 pb-4 border-t border-slate-200 pt-3 space-y-3">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                          <div>
                            <span className="text-slate-500 text-xs">Tipo documento</span>
                            <p className="font-medium">
                              {f.tipo_documento === "nota_credito" ? "Nota di credito" : "Fattura"}
                            </p>
                          </div>
                          <div>
                            <span className="text-slate-500 text-xs">Data emissione</span>
                            <p className="font-medium">{f.data_emissione ? new Date(f.data_emissione).toLocaleDateString("it-IT") : "-"}</p>
                          </div>
                          <div>
                            <span className="text-slate-500 text-xs">Scadenza</span>
                            <p className="font-medium">{f.data_scadenza ? new Date(f.data_scadenza).toLocaleDateString("it-IT") : "-"}</p>
                          </div>
                          <div>
                            <span className="text-slate-500 text-xs">Importo pagato</span>
                            <p className="font-medium text-emerald-600">€ {(f.importo_pagato || 0).toLocaleString("it-IT", { minimumFractionDigits: 2 })}</p>
                          </div>
                          <div>
                            <span className="text-slate-500 text-xs">Residuo</span>
                            <p className="font-medium text-red-600">€ {((f.totale || 0) - (f.importo_pagato || 0)).toLocaleString("it-IT", { minimumFractionDigits: 2 })}</p>
                          </div>
                        </div>

                        {f.tipo_documento !== "nota_credito" && f.nota_credito_numero && (
                          <div className="rounded-lg border border-purple-200 bg-purple-50 px-3 py-2 text-sm text-purple-900">
                            Fattura stornata/collegata a nota di credito{" "}
                            <span className="font-semibold">{f.nota_credito_numero}</span>.
                          </div>
                        )}
                        {f.tipo_documento === "nota_credito" && f.fattura_riferimento_numero && (
                          <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
                            Nota di credito riferita alla fattura{" "}
                            <span className="font-semibold">{f.fattura_riferimento_numero}</span>.
                          </div>
                        )}

                        {f.voci && f.voci.length > 0 && (
                          <div className="text-sm space-y-1">
                            <p className="text-xs text-slate-500 font-medium">Voci:</p>
                            {f.voci.map((v, vi) => {
                              const debAtt =
                                v.tipo === "deblistering_fattura_farmacia" && v.deblistering_documento_id;
                              return (
                                <div key={vi} className="flex justify-between gap-2 text-slate-600">
                                  <span className="min-w-0 flex-1">
                                    {debAtt ? (
                                      <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                        <span className="text-slate-800">Fattura farmacia (deblistering)</span>
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            downloadDeblisteringAllegato(v.deblistering_documento_id);
                                          }}
                                          className="inline-flex items-center gap-1 text-sm font-medium text-cyan-700 hover:text-cyan-900"
                                        >
                                          <Paperclip className="h-3.5 w-3.5 shrink-0" />
                                          Scarica DDT / allegato
                                        </button>
                                      </span>
                                    ) : (
                                      voceDescrizionePerUI(v)
                                    )}
                                  </span>
                                  <span className="shrink-0 tabular-nums">
                                    € {(v.quantita * v.prezzo_unitario).toLocaleString("it-IT", { minimumFractionDigits: 2 })}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        <div onClick={(e) => e.stopPropagation()}>
                          <FatturaAllegatiSection
                            fatturaId={f.id}
                            headers={headers}
                            canEdit={fatturaModificabile(f)}
                            onChanged={() => loadFatture({ silent: true })}
                          />
                        </div>

                        <div className="flex flex-wrap gap-2 pt-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-violet-200 text-violet-800"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (f.origine_documento === "fic_collegata") {
                                handleFatturaPdfFic(f);
                              } else {
                                handleFatturaPdf(f);
                              }
                            }}
                            disabled={fatturaPdfLoadingId === f.id || fatturaPdfLoadingId === `fic-${f.id}`}
                          >
                            {fatturaPdfLoadingId === f.id || fatturaPdfLoadingId === `fic-${f.id}` ? (
                              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                            ) : (
                              <Eye className="w-3 h-3 mr-1" />
                            )}
                            {f.origine_documento === "fic_collegata" ? "PDF FIC" : "PDF"}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownloadXml(f.id, f.numero);
                            }}
                          >
                            <Download className="w-3 h-3 mr-1" />
                            XML
                          </Button>
                          {["generata", "approvata", "rifiutata", "scartata"].includes(f.stato) &&
                            !inCodaSdi && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-blue-200 text-blue-700"
                              onClick={() => handleInviaSdi(f.id)}
                              disabled={sendingSdi === f.id || !canInviaSdi}
                            >
                              {sendingSdi === f.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                              Invia SDI
                            </Button>
                          )}
                          {inCodaSdi && (
                            <Badge className="bg-sky-100 text-sky-800 text-[10px] inline-flex items-center gap-1">
                              <Loader2 className="w-3 h-3 animate-spin" aria-hidden />
                              In coda SDI
                            </Badge>
                          )}
                          {f.tipo_documento !== "nota_credito" &&
                            ["inviata_sdi", "consegnata", "accettata"].includes(f.stato) &&
                            !f.nota_credito_id && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-purple-200 text-purple-800"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCreaNotaCredito(f);
                                }}
                                disabled={creatingNotaCredito === f.id}
                              >
                                {creatingNotaCredito === f.id ? (
                                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                ) : (
                                  <FileText className="w-3 h-3 mr-1" />
                                )}
                                Emetti NC
                              </Button>
                            )}
                          {f.tipo_documento !== "nota_credito" && f.stato === "stornata" && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-blue-200 text-blue-800"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRigeneraFattura(f);
                              }}
                              disabled={rigeneratingFattura === f.id}
                            >
                              {rigeneratingFattura === f.id ? (
                                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                              ) : (
                                <RefreshCw className="w-3 h-3 mr-1" />
                              )}
                              Rigenera
                            </Button>
                          )}
                          {(f.provider_id || f.fic_document_id) && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-amber-200 text-amber-800"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleScollegaFic(f);
                              }}
                              title="Rimuove il collegamento al documento FIC"
                            >
                              <Unlink className="w-3 h-3 mr-1" />
                              Scollega FIC
                            </Button>
                          )}
                          {fatturaModificabile(f) && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                title="Ricalcola totali senza bollo e rigenera XML"
                                onClick={() => handleRicalcolaFatturaSenzaBollo(f.id)}
                                disabled={savingFatturaPatch}
                              >
                                {savingFatturaPatch ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                                Ricalcola
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openEditVociDialog(f)}
                                disabled={savingFatturaPatch}
                              >
                                <Pencil className="w-3 h-3 mr-1" />
                                Voci
                              </Button>
                            </>
                          )}
                          {fatturaEliminabile(f, { isFic }) && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-700 border-red-200"
                              onClick={() => setDeleteTarget(buildDeleteTarget(f, isFic))}
                              disabled={savingFatturaPatch || deletingFattura}
                            >
                              <Trash2 className="w-3 h-3 mr-1" />
                              {fatturaEliminaSuFic(f, isFic) ? "Elimina su FIC" : "Elimina"}
                            </Button>
                          )}
                          {f.stato_pagamento !== "pagata" && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-emerald-200 text-emerald-700"
                                onClick={() => {
                                  setPagamentoFatturaId(f.id);
                                  setPagamentoForm({
                                    importo: String((f.totale || 0) - (f.importo_pagato || 0)),
                                    data_pagamento: new Date().toISOString().split("T")[0],
                                    metodo: "bonifico",
                                    note: "",
                                  });
                                  setShowPagamentoDialog(true);
                                }}
                              >
                                <CreditCard className="w-3 h-3 mr-1" />
                                Pagamento
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-amber-200 text-amber-700"
                                onClick={() => {
                                  setSollecitoFatturaId(f.id);
                                  setSollecitoForm({ messaggio: "", email_destinatario: "" });
                                  setShowSollecitoDialog(true);
                                }}
                              >
                                <Bell className="w-3 h-3 mr-1" />
                                Sollecito
                              </Button>
                            </>
                          )}
                        </div>

                        {f.pagamenti && f.pagamenti.length > 0 && (
                          <div className="text-sm">
                            <p className="text-xs text-slate-500 font-medium mb-1">Pagamenti registrati:</p>
                            {f.pagamenti.map((p, pi) => (
                              <div key={pi} className="flex justify-between text-slate-600 py-1 border-b border-slate-100 last:border-0">
                                <span>{new Date(p.data_pagamento).toLocaleDateString("it-IT")} - {p.metodo}</span>
                                <span className="font-medium text-emerald-600">€ {p.importo.toLocaleString("it-IT", { minimumFractionDigits: 2 })}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        {f.solleciti && f.solleciti.length > 0 && (
                          <div className="text-sm">
                            <p className="text-xs text-slate-500 font-medium mb-1">Solleciti inviati:</p>
                            {f.solleciti.map((s, si) => (
                              <div key={si} className="flex justify-between text-slate-600 py-1 text-xs">
                                <span>{new Date(s.data).toLocaleDateString("it-IT")} - {s.email_destinatario || "N/D"}</span>
                                <Badge className={s.inviato ? "bg-emerald-100 text-emerald-700 text-[10px]" : "bg-slate-100 text-slate-700 text-[10px]"}>
                                  {s.inviato ? "Inviato" : "Registrato"}
                                </Badge>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {fatture.length > 5 && section !== "storico" && (
                <Button
                  variant="ghost"
                  className="w-full mt-2 text-slate-500"
                  onClick={() => setShowStoricoAll(!showStoricoAll)}
                >
                  {showStoricoAll ? "Mostra meno" : `Mostra tutte (${fatture.length})`}
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Dialog: Genera Fattura */}
      <Dialog open={showGeneraDialog} onOpenChange={setShowGeneraDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-emerald-500" />
              Genera Fattura - {formatMese(meseSelezionato)}
            </DialogTitle>
            <DialogDescription>
              Scegli cosa includere in fattura. Puoi fatturare la retta per intero, in parte, solo come descrizione
              (importo zero) o escluderla del tutto.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="rounded-lg border border-slate-200 bg-slate-50/80 p-3 space-y-3">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="includi-retta"
                  checked={generaForm.includi_retta}
                  disabled={rettaCompletamenteFatturata}
                  onChange={e => setGeneraForm({ ...generaForm, includi_retta: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="includi-retta" className={rettaCompletamenteFatturata ? "text-slate-400" : ""}>
                  Includi retta mensile
                </Label>
              </div>
              {(anteprima?.retta_mensile > 0 || amministrativa?.retta_mensile > 0) && (
                <p className="text-xs text-slate-500 pl-7">
                  Retta configurata: €{" "}
                  {Number(anteprima?.retta_mensile ?? amministrativa?.retta_mensile ?? 0).toLocaleString("it-IT", {
                    minimumFractionDigits: 2,
                  })}
                  {rettaGiaFatturata > 0 && (
                    <>
                      {" "}
                      · Già fatturato: € {rettaGiaFatturata.toLocaleString("it-IT", { minimumFractionDigits: 2 })}
                    </>
                  )}
                  {rettaResidua != null && (
                    <>
                      {" "}
                      · Residuo: € {Math.max(0, rettaResidua).toLocaleString("it-IT", { minimumFractionDigits: 2 })}
                    </>
                  )}
                </p>
              )}
              {rettaCompletamenteFatturata && (
                <p className="text-xs text-amber-800 pl-7">
                  Retta del mese già interamente fatturata: puoi generare la fattura solo con le altre voci.
                </p>
              )}
              {generaForm.includi_retta && !rettaCompletamenteFatturata && (
                <div className="pl-7 space-y-3 border-t border-slate-200/80 pt-3">
                  <div>
                    <Label className="text-xs text-slate-500">Modalità retta in fattura</Label>
                    <Select
                      value={generaForm.retta_modalita}
                      onValueChange={v => setGeneraForm({ ...generaForm, retta_modalita: v })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="intera">Importo intero (residuo del mese)</SelectItem>
                        <SelectItem value="parziale">Importo personalizzato</SelectItem>
                        <SelectItem value="senza_importo">Solo descrizione (importo zero)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {generaForm.retta_modalita === "parziale" && (
                    <div>
                      <Label className="text-xs text-slate-500">Importo retta da fatturare (€)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        max={rettaResidua != null ? Math.max(0, rettaResidua) : undefined}
                        value={generaForm.importo_retta_parziale}
                        onChange={e =>
                          setGeneraForm({ ...generaForm, importo_retta_parziale: e.target.value })
                        }
                        className="mt-1"
                        placeholder={
                          rettaResidua != null
                            ? `Max ${Math.max(0, rettaResidua).toLocaleString("it-IT", { minimumFractionDigits: 2 })}`
                            : "Es. 500"
                        }
                      />
                    </div>
                  )}
                  {generaForm.retta_modalita === "senza_importo" && (
                    <p className="text-xs text-slate-500">
                      La riga soggiorno comparirà in fattura con importo € 0,00 (utile se l&apos;importo è a carico
                      di un ente o viene gestito separatamente).
                    </p>
                  )}
                  {vociRettaConfig.length > 0 && generaForm.retta_modalita !== "senza_importo" && (
                    <div className="space-y-2">
                      <Label className="text-xs text-slate-500">Voci retta da includere</Label>
                      {vociRettaConfig.map(voce => {
                        const checked = generaForm.voci_retta_incluse.includes(voce.id);
                        return (
                          <div key={voce.id} className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              id={`voce-retta-${voce.id}`}
                              checked={checked}
                              onChange={e => {
                                const next = e.target.checked
                                  ? [...generaForm.voci_retta_incluse, voce.id]
                                  : generaForm.voci_retta_incluse.filter(id => id !== voce.id);
                                setGeneraForm({ ...generaForm, voci_retta_incluse: next });
                              }}
                              className="rounded"
                            />
                            <Label htmlFor={`voce-retta-${voce.id}`} className="font-normal cursor-pointer">
                              {voce.nome || "Voce"} ({Number(voce.percentuale || 0).toLocaleString("it-IT")}%)
                            </Label>
                          </div>
                        );
                      })}
                      <p className="text-[11px] text-slate-400">
                        Deseleziona le voci che non devono comparire in questa fattura. La quota base soggiorno segue
                        le percentuali rimanenti.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="includi-pn"
                checked={generaForm.includi_prima_nota}
                onChange={e => setGeneraForm({ ...generaForm, includi_prima_nota: e.target.checked })}
                className="rounded"
              />
              <Label htmlFor="includi-pn">Includi voci da Prima Nota</Label>
            </div>
            {depImportoConfigured && (
              <div className="rounded-lg border border-slate-200 bg-slate-50/80 p-3 space-y-2">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="includi-dep"
                    checked={generaForm.includi_deposito_cauzionale}
                    disabled={depositoGiaFatturato}
                    onChange={e =>
                      setGeneraForm({ ...generaForm, includi_deposito_cauzionale: e.target.checked })
                    }
                    className="rounded mt-0.5"
                  />
                  <div className="min-w-0 flex-1">
                    <Label
                      htmlFor="includi-dep"
                      className={`text-sm font-medium leading-snug cursor-pointer ${depositoGiaFatturato ? "text-slate-400" : "text-slate-800"}`}
                    >
                      Includi deposito cauzionale
                    </Label>
                    <p className="text-xs text-slate-500 mt-1">
                      Voce in fattura: Deposito cauzionale infruttifero a garanzia degli obblighi contrattuali (
                      €{" "}
                      {Number(amministrativa?.deposito_cauzionale).toLocaleString("it-IT", {
                        minimumFractionDigits: 2,
                      })}
                      ). Può comparire una sola volta sulle fatture dell&apos;ospite.
                    </p>
                    {depositoGiaFatturato && fatturaConDeposito?.numero && (
                      <p className="text-xs text-amber-800 mt-1">
                        Già incluso nella fattura {fatturaConDeposito.numero}.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
            {config?.provider === "fatture_in_cloud" && ficSedeGenera && (
              <div className="rounded-lg border border-sky-200 bg-sky-50/80 p-3 space-y-2 text-sm">
                <p className="text-sky-900 font-medium">Indirizzo su Fatture in Cloud</p>
                <p className="text-slate-700">{formatFicSedeDisplay(ficSedeGenera)}</p>
                <p className="text-xs text-slate-500">
                  Manca in anagrafica Affidya. Puoi importarlo e salvarlo nell&apos;anagrafica ospite.
                </p>
                <Button
                  type="button"
                  size="sm"
                  className="bg-sky-600 hover:bg-sky-700"
                  disabled={salvandoSedeFic}
                  onClick={handleApplicaSedeFicGenera}
                >
                  {salvandoSedeFic ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
                  Importa da FIC e salva in anagrafica
                </Button>
              </div>
            )}
            <div>
              <Label className="text-xs text-slate-500">Codice Fiscale Destinatario</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={generaForm.destinatario_codice_fiscale}
                  onChange={e => setGeneraForm({ ...generaForm, destinatario_codice_fiscale: e.target.value })}
                  placeholder="RSSMRA80A01H501U"
                  className="font-mono"
                />
                {config?.provider === "fatture_in_cloud" && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="shrink-0 gap-1.5"
                    onClick={() => handleFicLookup(generaForm.destinatario_codice_fiscale)}
                    disabled={ficLookup.loading || !generaForm.destinatario_codice_fiscale?.trim()}
                    title="Cerca anagrafica su Fatture in Cloud"
                  >
                    {ficLookup.loading
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <Search className="w-3.5 h-3.5" />}
                    FIC
                  </Button>
                )}
              </div>
            </div>

            {/* FIC lookup result banner */}
            {config?.provider === "fatture_in_cloud" && ficLookup.items !== null && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2 text-sm">
                {ficLookup.error ? (
                  <p className="text-amber-700 text-xs">{ficLookup.error}</p>
                ) : ficLookup.items.length === 0 ? (
                  <p className="text-slate-500 text-xs">Nessun cliente trovato su FIC per questo CF.</p>
                ) : (
                  ficLookup.items.map((c, i) => (
                    <div key={i} className="flex items-start justify-between gap-3">
                      <div className="min-w-0 space-y-0.5">
                        <p className="font-medium truncate">{c.nome || "—"}</p>
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-slate-500">
                          {c.partita_iva && <span>P.IVA: <span className="font-mono">{c.partita_iva}</span></span>}
                          {normalizeCodiceSdi(c.codice_sdi) !== "0000000" && (
                            <span>SDI: <span className="font-mono">{normalizeCodiceSdi(c.codice_sdi)}</span></span>
                          )}
                          {c.pec && <span>PEC: {c.pec}</span>}
                          {(c.citta || c.indirizzo) && (
                            <span>{[c.indirizzo, c.cap, c.citta, c.provincia].filter(Boolean).join(", ")}</span>
                          )}
                        </div>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        className="shrink-0 h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white gap-1"
                        onClick={() => applyFicClient(c)}
                      >
                        <CheckCircle2 className="w-3 h-3" />
                        Applica
                      </Button>
                    </div>
                  ))
                )}
              </div>
            )}

            <div>
              <Label className="text-xs text-slate-500">Partita IVA Destinatario (se azienda)</Label>
              <Input
                value={generaForm.destinatario_partita_iva}
                onChange={e => setGeneraForm({ ...generaForm, destinatario_partita_iva: e.target.value })}
                placeholder="01234567890"
                className="mt-1 font-mono"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-500">PEC Destinatario</Label>
                <Input
                  value={generaForm.destinatario_pec}
                  onChange={e => setGeneraForm({ ...generaForm, destinatario_pec: e.target.value })}
                  placeholder="pec@esempio.it"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Codice SDI</Label>
                <Input
                  value={generaForm.destinatario_sdi}
                  onChange={e => setGeneraForm({ ...generaForm, destinatario_sdi: e.target.value })}
                  placeholder="0000000"
                  className="mt-1 font-mono"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Email destinatario</Label>
              <Input
                type="email"
                value={generaForm.destinatario_email}
                onChange={e => setGeneraForm({ ...generaForm, destinatario_email: e.target.value })}
                placeholder="familiare@esempio.it"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Indirizzo Destinatario</Label>
              <Input
                value={generaForm.destinatario_indirizzo}
                onChange={e => setGeneraForm({ ...generaForm, destinatario_indirizzo: e.target.value })}
                placeholder="Via Roma, 1"
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-1">
                <Label className="text-xs text-slate-500">CAP</Label>
                <Input
                  value={generaForm.destinatario_cap}
                  onChange={e => setGeneraForm({ ...generaForm, destinatario_cap: e.target.value })}
                  placeholder="00100"
                  className="mt-1 font-mono"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Città</Label>
                <Input
                  value={generaForm.destinatario_citta}
                  onChange={e => setGeneraForm({ ...generaForm, destinatario_citta: e.target.value })}
                  placeholder="Roma"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Prov.</Label>
                <Input
                  value={generaForm.destinatario_provincia}
                  onChange={e => setGeneraForm({ ...generaForm, destinatario_provincia: e.target.value })}
                  placeholder="RM"
                  maxLength={2}
                  className="mt-1 font-mono uppercase"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-500">Modalità Pagamento</Label>
                <Select
                  value={generaForm.modalita_pagamento}
                  onValueChange={v => setGeneraForm({ ...generaForm, modalita_pagamento: v })}
                >
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MP05">Bonifico</SelectItem>
                    <SelectItem value="MP01">Contanti</SelectItem>
                    <SelectItem value="MP08">Carta di credito</SelectItem>
                    <SelectItem value="MP02">Assegno</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Scadenza (giorni)</Label>
                <Input
                  type="number"
                  value={generaForm.scadenza_giorni}
                  onChange={e => setGeneraForm({ ...generaForm, scadenza_giorni: parseInt(e.target.value) || 30 })}
                  className="mt-1"
                />
              </div>
            </div>

            {anteprima && (
              <div className="bg-emerald-50 rounded-lg p-3 text-sm">
                <div className="flex justify-between font-semibold">
                  <span>Totale stimato</span>
                  <span className="text-emerald-700">€ {anteprima.totale.toLocaleString("it-IT", { minimumFractionDigits: 2 })}</span>
                </div>
                <p className="text-xs text-slate-500 mt-1">{anteprima.voci.length} voci</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGeneraDialog(false)} disabled={generating}>Annulla</Button>
            <Button onClick={handleGenera} disabled={generating || !(anteprima?.voci?.length > 0)} className="bg-emerald-600 hover:bg-emerald-700">
              {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
              Genera
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Registra Pagamento */}
      <Dialog open={showPagamentoDialog} onOpenChange={setShowPagamentoDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-emerald-500" />
              Registra Pagamento
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs text-slate-500">Importo (€)</Label>
              <Input
                type="number"
                step="0.01"
                value={pagamentoForm.importo}
                onChange={e => setPagamentoForm({ ...pagamentoForm, importo: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Data Pagamento</Label>
              <Input
                type="date"
                value={pagamentoForm.data_pagamento}
                onChange={e => setPagamentoForm({ ...pagamentoForm, data_pagamento: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Metodo</Label>
              <Select value={pagamentoForm.metodo} onValueChange={v => setPagamentoForm({ ...pagamentoForm, metodo: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bonifico">Bonifico</SelectItem>
                  <SelectItem value="contanti">Contanti</SelectItem>
                  <SelectItem value="carta">Carta</SelectItem>
                  <SelectItem value="assegno">Assegno</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Note</Label>
              <Input
                value={pagamentoForm.note}
                onChange={e => setPagamentoForm({ ...pagamentoForm, note: e.target.value })}
                className="mt-1"
                placeholder="Opzionale"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPagamentoDialog(false)} disabled={savingPagamento}>Annulla</Button>
            <Button onClick={handleRegistraPagamento} disabled={savingPagamento} className="bg-emerald-600 hover:bg-emerald-700">
              {savingPagamento ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Registra
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Invia Sollecito */}
      <Dialog open={showSollecitoDialog} onOpenChange={setShowSollecitoDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-amber-500" />
              Invia Sollecito
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs text-slate-500">Email Destinatario</Label>
              <Input
                type="email"
                value={sollecitoForm.email_destinatario}
                onChange={e => setSollecitoForm({ ...sollecitoForm, email_destinatario: e.target.value })}
                className="mt-1"
                placeholder="Lascia vuoto per usare contatto familiare"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Messaggio</Label>
              <Textarea
                value={sollecitoForm.messaggio}
                onChange={e => setSollecitoForm({ ...sollecitoForm, messaggio: e.target.value })}
                className="mt-1"
                rows={3}
                placeholder="Messaggio personalizzato (opzionale)"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSollecitoDialog(false)} disabled={sendingSollecito}>Annulla</Button>
            <Button onClick={handleInviaSollecito} disabled={sendingSollecito} className="bg-amber-600 hover:bg-amber-700">
              {sendingSollecito ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Invia
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-left">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
              {deleteTarget?.eliminaFic ? "Eliminare su FIC?" : "Eliminare la fattura?"}
            </DialogTitle>
            <DialogDescription className="text-left">
              <span className="font-mono text-slate-800">{deleteTarget?.numero}</span>
              {" — "}operazione definitiva.
              {deleteTarget?.eliminaFic ? (
                <>
                  {" "}Il documento verrà eliminato su Fatture in Cloud e il record collegato in Affidya.
                </>
              ) : (
                <>
                  {" "}Le voci Prima Nota collegate torneranno disponibili per una nuova fattura.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setDeleteTarget(null)} disabled={deletingFattura}>
              Annulla
            </Button>
            <Button type="button" variant="destructive" onClick={confirmDeleteFattura} disabled={deletingFattura}>
              {deletingFattura ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {deleteTarget?.eliminaFic ? "Elimina su FIC" : "Elimina"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ModificaVociFatturaDialog
        open={editVociDialogOpen}
        onOpenChange={(open) => {
          setEditVociDialogOpen(open);
          if (!open) setEditVociTarget(null);
        }}
        initialVoci={editVociTarget?.voci}
        saving={savingFatturaPatch}
        onSave={handleSaveVociFromEditor}
      />

      <CollegaFicDialog
        open={ficDialogOpen}
        onOpenChange={setFicDialogOpen}
        headers={headers}
        meseControllo={meseSelezionato}
        strutturaIdForConfig={strutturaIdForConfig}
        fascicoli={[{ id: fascicoloId, anagrafica, amministrativa }]}
        initialFascicoloId={fascicoloId}
        onSuccess={async () => {
          await loadFatture();
          await loadAnteprima();
          setFicDialogOpen(false);
        }}
      />

      <ConfermaSedeFicDialog
        open={!!ficSedeConfirm}
        onOpenChange={(open) => !open && setFicSedeConfirm(null)}
        ficSede={ficSedeConfirm?.ficSede}
        numeroFattura={ficSedeConfirm?.numero}
        loading={confirmingFicSede}
        onConfirm={handleConfermaSedeFicInvio}
      />

      <Dialog open={pdfPreviewOpen} onOpenChange={handlePdfPreviewDialogChange}>
        <DialogContent className="max-w-4xl h-[85vh] p-0">
          <DialogHeader className="px-4 py-3 border-b border-slate-100">
            <DialogTitle className="text-sm">{pdfPreviewTitle}</DialogTitle>
          </DialogHeader>
          {pdfPreviewUrl ? (
            <iframe
              src={pdfPreviewUrl}
              className="w-full flex-1 h-[calc(85vh-60px)]"
              title={pdfPreviewTitle}
            />
          ) : (
            <div className="flex items-center justify-center h-[calc(85vh-60px)]">
              <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const REGIMI_IVA_LABELS = {
  esente_art10: "Esente Art.10",
  iva_4: "IVA 4%",
  iva_22: "IVA 22%",
  esclusa_art15: "Escl. Art.15",
};
